// Native
import { useState } from "react"
import { Link } from "react-router-dom"

// Components
import { Badge, Button, ButtonGroup, Divider, Flex, HStack, ModalBody, ModalFooter, Stack, StackDivider, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useDisclosure } from "@chakra-ui/react"
import { Dev, Icon, IconButton, Label, Modal, Scope, Show, Tag, Unverified } from "components"

// Config
import { Enums, Moment } from "config"

// Hooks
import { useOrder, useScreens } from "hooks"

import * as Screens from 'screens'

// Parts
import Itens from "./itens"
import Transactions from "./transactions"
import Details from "./details"
import Shipping from "./shipping"
import History from "./history"
import Files from "pages/common/files"
import Fiscal from "./fiscal"
import Cancel from "./cancel"
import Approve from "./approve"
import Extract from "./extract"
import SetShipping from "./setShipping"
import Status from "./status"

export default function Order({ data, ...props }){

    const { order:orderData, setOrder } = useScreens()
    const order = useOrder(orderData?.id)
    const [ loading, setLoading ] = useState(false)
    const cancel = useDisclosure()
    const approve = useDisclosure()
    const shipping = useDisclosure()
    const status = useDisclosure()

    const onClose = () => setOrder(null)

    if(!!orderData && order.isLoading) return <Screens.Loading isOpen={!!orderData} onClose={onClose} text="Carregando dados do pedido..." />

    if(order.isError && order.error?.response?.status === 403) return (
        <Modal title="Acesso negado" isOpen={!!orderData} onClose={onClose} size="sm">
            <ModalBody>
                <Text color="orange.500">Você não tem acesso a este pedido.</Text>
            </ModalBody>
            <ModalFooter>
                <Button size="sm" onClick={onClose}>Fechar</Button>
            </ModalFooter>
        </Modal>
    )

    return(
    <>
    <Modal title={`Pedido #${order.data?.cod}`} isOpen={!!orderData} isLoading={loading} onClose={onClose} size="4xl" {...props}>

        <ModalBody>

            <HStack>
                <Badge children={order.data?.status_label} colorScheme={Enums.OrderStatus.data(order.data?.status).colorScheme} />
                <Badge children={`Pedido de ${order.data?.type_label}`} />
                <Tag size="sm" label={Moment(order.data?.requested_at).format('LLL')}>{Moment(order.data?.requested_at).calendar()}</Tag>
            </HStack>

            <Stack spacing={4} mt={4}>

                <HStack justifyContent="space-between">
                    {/* <Link to={`/consultants/${order.data?.consultant?.id}`}><Label label="Cliente:" children={order.data?.distributor_to?.name ?? order.data?.consultant?.customer?.name} fontSize="md" /></Link> */}
                    <Show visible={!!order.data?.consultant}><Link to={`/consultants/${order.data?.consultant?.id}`}><Label label="Cliente:" children={order.data?.consultant?.customer?.name} fontSize="md" /></Link></Show>
                    <Show visible={!!order.data?.distributor_to}><Link to={`/distributors/${order.data?.distributor_to?.id}`}><Label label="Cliente:" children={order.data?.distributor_to?.name} fontSize="md" /></Link></Show>
                    <Show visible={!!order.data?.distributor_from}><Link to={`/distributors/${order.data?.distributor_from?.id}`}><Label label="Núcleo:" children={order.data?.distributor_from?.name} fontSize="md" /></Link></Show>
                    <Show visible={!order.data?.distributor_from}><Link to={`/consultants/${order.data?.consultant_from?.id}`}><Label label="Consultor Parceiro:" children={order.data?.consultant_from?.customer?.name} fontSize="md" /></Link></Show>
                </HStack>

                <Divider />

                <Show visible={!!order.data?.is_legacy}><Unverified /></Show>

                <Tabs variant="solid-rounded" size="sm" colorScheme="orange">
                    <TabList>
                        <Tab>Detalhes</Tab>
                        <Tab>Itens</Tab>
                        <Tab>Pagamento</Tab>
                        { order.data?.shipping_type === Enums.OrderShippingType.SHIPPING && <Tab>Entrega</Tab> }
                        <Tab>Fiscal</Tab>
                        <Tab>Histórico</Tab>
                        <Tab>Arquivos</Tab>
                        <Tab isDisabled={[2,4].includes(order.data?.type)}>Extrato</Tab>
                    </TabList>
                    <TabPanels>
                        <TabPanel><Details order={order} /></TabPanel>
                        <TabPanel><Itens order={order} /></TabPanel>
                        <TabPanel><Transactions data={order.data} /></TabPanel>
                        { order.data?.shipping_type === Enums.OrderShippingType.SHIPPING && <TabPanel><Shipping order={order} /></TabPanel> }
                        <TabPanel><Fiscal data={order.data} /></TabPanel>
                        <TabPanel><History data={order.data} /></TabPanel>
                        <TabPanel><Files data={order.data} model="orders" title="Arquivos do pedido" options={{ order_id: order?.data?.id }} /></TabPanel>
                        <TabPanel><Extract data={order.data} /></TabPanel>
                    </TabPanels>
                </Tabs>
            </Stack>

        </ModalBody>

        <ModalFooter>
            <Flex w="100%" justifyContent="space-between">
                <HStack divider={<StackDivider />}>
                    {/* <Pressable>
                        <AvatarGroup size="xs" max={4}>
                            <Avatar name='Ryan Florence' src='https://bit.ly/ryan-florence' />
                            <Avatar name='Segun Adebayo' src='https://bit.ly/sage-adebayo' />
                            <Avatar name='Kent Dodds' src='https://bit.ly/kent-c-dodds' />
                            <Avatar name='Prosper Otemuyiwa' src='https://bit.ly/prosper-baba' />
                            <Avatar name='Christian Nwamba' src='https://bit.ly/code-beast' />
                        </AvatarGroup>
                    </Pressable> */}

                    <Text fontSize="xs" color="GrayText" children={`Última atualização ${Moment(order.data?.updated_at).fromNow()}`} />
                </HStack>

                <ButtonGroup size="xs" isDisabled={order.isFetching}>

                    <Scope role="orders_update">

                        {/* Ações para o status: Em Análise */}
                        <Show visible={order.data?.status === Enums.OrderStatus.ANALYZING}>
                            <Button children="Aprovar pedido" colorScheme="teal" leftIcon={<Icon name="check" />} onClick={approve.onOpen} />
                            <IconButton label="Cancelar pedido" colorScheme="red" icon="cancel" onClick={cancel.onOpen} />
                        </Show>

                        <IconButton label="Tipo de envio" icon="transit" isDisabled={order.isFetching} onClick={shipping.onOpen} />
                        <IconButton label="Alterar status" icon="status" isDisabled={order.isFetching} onClick={status.onOpen} />

                    </Scope>

                    <IconButton label="Imprimir" icon="print" isDisabled={order.isFetching} onClick={() => window.open(order.data?.pdf_url)} />
                    <IconButton label="Atualizar" icon="refresh" isLoading={order.isFetching} onClick={order.refetch} />
                    <Dev data={order.data} />
                </ButtonGroup>
            </Flex>
        </ModalFooter>
    </Modal>

    {/* Screens */}
    <Cancel data={order.data} {...cancel} />
    <Approve data={order.data} {...approve} />
    <SetShipping data={order.data} {...shipping} />
    <Status data={order.data} {...status} />
    </>
    )
}
